import { AsyncPipe } from '@angular/common';
import { Component, HostListener, inject, OnDestroy, OnInit, signal } from '@angular/core';
import { MatListModule } from '@angular/material/list';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatTooltipModule } from '@angular/material/tooltip';
import { RouterModule } from '@angular/router';
import { HasPermissionDirective } from '@core/directives/has-permission.directive';
import { AuthService } from '@core/services/auth.service';
import { TranslateModule } from '@ngx-translate/core';
import { BaseComponent } from '@shared/components/base.component';
import { IconBadgeComponent } from '@shared/components/icon-badge/icon-badge.component';
import { IconComponent } from '@shared/components/icon/icon.component';
import { SETTINGS_ACCESS_PERMISSION_GROUPS } from '@shared/constants/settings-access.constants';
import { Subscription } from 'rxjs';
import { AiConnectorTeaserComponent } from './ai-connector-teaser/ai-connector-teaser.component';
import { UserGroupSelectComponent } from './user-group-select/user-group-select.component';

@Component({
  imports: [
    MatSidenavModule,
    MatListModule,
    RouterModule,
    IconBadgeComponent,
    AsyncPipe,
    TranslateModule,
    HasPermissionDirective,
    UserGroupSelectComponent,
    MatTooltipModule,
    IconComponent,
    AiConnectorTeaserComponent,
  ],
  selector: 'app-sidebar',
  styleUrls: ['./sidebar.component.scss'],
  templateUrl: './sidebar.component.html',
})
export class SidebarComponent extends BaseComponent implements OnInit, OnDestroy {
  /** Gates the "Settings" nav item and its separator below - see the constant's own doc comment
   * for the other places (the /account-panel route guard) sharing this same permission pair. */
  public readonly settingsAccessPermissionGroups = SETTINGS_ACCESS_PERMISSION_GROUPS;

  public readonly isExpanded = signal<boolean>(false);

  public readonly authService = inject(AuthService);

  public userName: string | null = null;

  private readonly storageKey: string = 'sidebarExpanded';

  private hasUserPreference = false;

  private userNameSubscription: Subscription | null = null;

  constructor() {
    super();

    this.userNameSubscription = this.authService.currentDbUser$.subscribe((user) => {
      this.userName = user?.name?.split(' ')[0] ?? '';
    });
  }

  public ngOnDestroy(): void {
    if (this.userNameSubscription) {
      this.userNameSubscription.unsubscribe();
    }
  }

  public ngOnInit(): void {
    this.loadExpandedFromStorage();

    if (!this.hasUserPreference) {
      this.checkScreenWidth();
    }
  }

  @HostListener('window:resize')
  public onResize(): void {
    if (!this.hasUserPreference) {
      this.checkScreenWidth();
    }
  }

  private checkScreenWidth(): void {
    const shouldExpandByDefault: boolean = window.innerWidth >= 1400;

    this.isExpanded.set(shouldExpandByDefault);
  }

  public toggleSidebar(): void {
    const nextState = !this.isExpanded();

    this.isExpanded.set(nextState);
    this.hasUserPreference = true;

    this.saveExpandedToStorage(nextState);
  }

  private loadExpandedFromStorage(): void {
    try {
      const saved: string | null = localStorage.getItem(this.storageKey);

      if (saved !== null) {
        const parsed: boolean = saved === '1';

        this.isExpanded.set(parsed);
        this.hasUserPreference = true;
      }
    } catch {
      /* Ignore storage errors */
    }
  }

  private saveExpandedToStorage(value: boolean): void {
    try {
      localStorage.setItem(this.storageKey, value ? '1' : '0');
    } catch {
      /* Ignore storage errors */
    }
  }
}
